import { defineComponent as _defineComponent } from 'vue';
import { library } from "@fortawesome/fontawesome-svg-core";
import { faTrashAlt } from "@fortawesome/free-regular-svg-icons";
import { faCompressAlt, faObjectGroup, faPalette } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/vue-fontawesome";
import { useFocusWithin } from "@vueuse/core";
import { BButton, BButtonGroup } from "bootstrap-vue";
import { sanitize } from "dompurify";
import { computed, onMounted, reactive, ref, watch } from "vue";
import { AxisAlignedBoundingBox } from "@/components/Workflow/Editor/modules/geometry";
import { useWorkflowStores } from "@/composables/workflowStores";
import { brighterColors, darkenedColors } from "./colors";
import { useResizable } from "./useResizable";
import { selectAllText } from "./utilities";
import ColorSelector from "./ColorSelector.vue";
import DraggablePan from "@/components/Workflow/Editor/DraggablePan.vue";
export default /*#__PURE__*/ _defineComponent({
    __name: 'FrameComment',
    props: {
        comment: null,
        rootOffset: null,
        scale: null,
        readonly: { type: Boolean }
    },
    emits: ["change", "resize", "move", "pan-by", "remove", "set-color"],
    setup(__props, { emit }) {
        const props = __props;
        library.add(faObjectGroup, faTrashAlt, faPalette, faCompressAlt);
        const resizeContainer = ref();
        useResizable(resizeContainer, computed(() => props.comment.size), ([width, height]) => {
            emit("resize", [width, height]);
        });
        function escapeAndSanitize(text) {
            return sanitize(text, { ALLOWED_TAGS: [] }).replace(/(?:^(\s|&nbsp;)+)|(?:(\s|&nbsp;)+$)/g, "");
        }
        const editableElement = ref();
        function getInnerText() {
            var _a;
            const element = editableElement.value;
            if (element) {
                const value = (_a = element.innerHTML) !== null && _a !== void 0 ? _a : "";
                return escapeAndSanitize(value);
            }
            else {
                return "";
            }
        }
        function saveText() {
            emit("change", Object.assign(Object.assign({}, props.comment.data), { title: getInnerText() }));
        }
        const showColorSelector = ref(false);
        const rootElement = ref();
        const { focused } = useFocusWithin(rootElement);
        watch(() => focused.value, () => {
            if (!focused.value) {
                showColorSelector.value = false;
            }
        });
        function onClick() {
            var _a;
            (_a = editableElement.value) === null || _a === void 0 ? void 0 : _a.focus();
        }
        function onSetColor(color) {
            emit("set-color", color);
        }
        const { stateStore, stepStore, commentStore } = useWorkflowStores();
        function getStepsInBounds(bounds) {
            const steps = [];
            Object.values(stepStore.steps).forEach((step) => {
                const rect = stateStore.stepPosition[step.id];
                if (rect && step.position) {
                    const stepRect = {
                        x: step.position.left,
                        y: step.position.top,
                        width: rect.width,
                        height: rect.height,
                    };
                    if (bounds.contains(stepRect)) {
                        steps.push(step);
                    }
                }
            });
            return steps;
        }
        function getCommentsInBounds(bounds) {
            const comments = [];
            commentStore.comments.forEach((comment) => {
                const commentRect = {
                    x: comment.position[0],
                    y: comment.position[1],
                    width: comment.size[0],
                    height: comment.size[1],
                };
                if (comment !== props.comment && bounds.contains(commentRect)) {
                    comments.push(comment);
                }
            });
            return comments;
        }
        let stepsInBounds = [];
        let commentsInBounds = [];
        const stepStartOffsets = new Map();
        const commentStartOffsets = new Map();
        function getAABB() {
            const aabb = new AxisAlignedBoundingBox();
            aabb.x = props.comment.position[0];
            aabb.y = props.comment.position[1];
            aabb.width = props.comment.size[0];
            aabb.height = props.comment.size[1];
            return aabb;
        }
        function onDragStart() {
            const aabb = getAABB();
            stepsInBounds = getStepsInBounds(aabb);
            commentsInBounds = getCommentsInBounds(aabb);
            stepsInBounds.forEach((step) => {
                stepStartOffsets.set(step.id, [step.position.left - aabb.x, step.position.top - aabb.y]);
            });
            commentsInBounds.forEach((comment) => {
                commentStartOffsets.set(comment.id, [comment.position[0] - aabb.x, comment.position[1] - aabb.y]);
            });
        }
        function onDragEnd() {
            saveText();
            stepsInBounds = [];
            commentsInBounds = [];
            stepStartOffsets.clear();
            commentStartOffsets.clear();
        }
        function onMove(position) {
            stepsInBounds.forEach((step) => {
                var _a;
                const stepPosition = { left: 0, top: 0 };
                const offset = (_a = stepStartOffsets.get(step.id)) !== null && _a !== void 0 ? _a : [0, 0];
                stepPosition.left = position.x + offset[0];
                stepPosition.top = position.y + offset[1];
                stepStore.updateStep(Object.assign(Object.assign({}, step), { position: stepPosition }));
            });
            commentsInBounds.forEach((comment) => {
                var _a;
                const offset = (_a = commentStartOffsets.get(comment.id)) !== null && _a !== void 0 ? _a : [0, 0];
                const commentPosition = [position.x + offset[0], position.y + offset[1]];
                commentStore.changePosition(comment.id, commentPosition);
            });
            emit("move", [position.x, position.y]);
        }
        function onDoubleClick() {
            if (editableElement.value) {
                selectAllText(editableElement.value);
            }
        }
        function onFitToContent() {
            const aabb = getAABB();
            stepsInBounds = getStepsInBounds(aabb);
            commentsInBounds = getCommentsInBounds(aabb);
            const targetAABB = new AxisAlignedBoundingBox();
            stepsInBounds.forEach((step) => {
                const rect = stateStore.stepPosition[step.id];
                if (rect && step.position) {
                    const stepRect = {
                        x: step.position.left,
                        y: step.position.top,
                        width: rect.width,
                        height: rect.height,
                    };
                    targetAABB.fitRectangle(stepRect);
                }
            });
            commentsInBounds.forEach((comment) => {
                const commentRect = {
                    x: comment.position[0],
                    y: comment.position[1],
                    width: comment.size[0],
                    height: comment.size[1],
                };
                targetAABB.fitRectangle(commentRect);
            });
            targetAABB.expand(20);
            targetAABB.y -= 20;
            emit("move", [targetAABB.x, targetAABB.y]);
            emit("resize", [targetAABB.width, targetAABB.height]);
        }
        const cssVariables = computed(() => {
            const vars = {};
            if (props.comment.color !== "none") {
                vars["--primary-color"] = darkenedColors[props.comment.color];
                vars["--secondary-color"] = brighterColors[props.comment.color];
            }
            return vars;
        });
        onMounted(() => {
            if (commentStore.isJustCreated(props.comment.id) && editableElement.value) {
                selectAllText(editableElement.value);
            }
        });
        return { __sfc: true, props, emit, resizeContainer, escapeAndSanitize, editableElement, getInnerText, saveText, showColorSelector, rootElement, focused, onClick, onSetColor, stateStore, stepStore, commentStore, getStepsInBounds, getCommentsInBounds, stepsInBounds, commentsInBounds, stepStartOffsets, commentStartOffsets, getAABB, onDragStart, onDragEnd, onMove, onDoubleClick, onFitToContent, cssVariables, FontAwesomeIcon, BButton, BButtonGroup, reactive, ColorSelector, DraggablePan };
    }
});
